import { AnimatePresence, motion } from 'motion/react';
import { AppIcon } from '../app/AppIcon.js';
import { CoreconfMark } from '../brand/CoreconfMark.js';
import { useUpdateStore } from './useUpdate.js';
import type { DeferReason } from './releaseChannel.js';
import { useModalBehavior } from '../app/useModalBehavior.js';

const DEFER_COPY: Record<DeferReason, string> = {
  'voice-call': 'Görüşme bitince güncellenecek.',
  'unsent-draft': 'Gönderilmemiş mesajın var — bitirince güncellenecek.',
  'user-postponed': 'Hazır olduğunda güncelle.',
};

const EASE_SWIM = [0.16, 1, 0.3, 1] as const;

/**
 * Optional update: a strip inside the conversation column.
 *
 * Rendered in normal flow rather than as a floating overlay. An overlay in this
 * layout necessarily covers something — first the member list, then the
 * channel's security badge — and a notice that hides the badge telling you
 * whether the operator can read your messages is a notice that costs more than
 * it delivers. In flow it displaces content instead of hiding it, and the
 * displacement is animated so the arrival still reads as an event.
 */
export function UpdateBanner(): React.ReactElement {
  const urgency = useUpdateStore((s) => s.urgency);
  const manifest = useUpdateStore((s) => s.manifest);
  const dismissed = useUpdateStore((s) => s.dismissed);
  const deferredReason = useUpdateStore((s) => s.deferredReason);
  const apply = useUpdateStore((s) => s.apply);
  const dismiss = useUpdateStore((s) => s.dismiss);

  const show = urgency === 'optional' && !dismissed && manifest !== null;

  return (
    <AnimatePresence initial={false}>
      {show ? (
        <motion.div
          key="update-bar"
          className="update-bar"
          role="status"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.42, ease: EASE_SWIM }}
        >
          <div className="update-bar-inner">
            <CoreconfMark size={24} title="" />
            <span className="update-bar-text">
              <strong>Sürüm {manifest.version}</strong> hazır.
              <span className="update-bar-note">
                {' '}
                {deferredReason !== null
                  ? DEFER_COPY[deferredReason]
                  : 'Güncelleme anında uygulanır, taslakların korunur.'}
              </span>
            </span>
            <button className="update-cta" onClick={() => apply()}>
              <AppIcon name="refresh" size={14} hole="transparent" />
              Güncelle
            </button>
            <button className="icon-btn" onClick={dismiss} aria-label="Şimdilik gizle">
              <AppIcon name="close" size={14} hole="transparent" />
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Required update: blocking, and deliberately hard to mistake for the banner.
 *
 * Reserved for releases below `minimumSupportedVersion` — a security fix or a
 * protocol break. Using it for ordinary releases is how users learn that a
 * blocking dialog means nothing.
 */
export function UpdateGate(): React.ReactElement {
  const urgency = useUpdateStore((s) => s.urgency);
  const manifest = useUpdateStore((s) => s.manifest);
  const apply = useUpdateStore((s) => s.apply);

  return (
    <AnimatePresence>
      {urgency === 'required' && manifest !== null ? (
        <RequiredUpdateDialog
          key="scrim"
          version={manifest.version}
          summary={manifest.summary}
          onApply={() => apply(true)}
        />
      ) : null}
    </AnimatePresence>
  );
}

function RequiredUpdateDialog({
  version,
  summary,
  onApply,
}: {
  version: string;
  summary?: string | undefined;
  onApply: () => void;
}): React.ReactElement {
  const panelRef = useModalBehavior<HTMLDivElement>(() => {}, {
    initialFocus: '.update-cta',
    closeOnEscape: false,
  });

  return (
    <motion.div
      className="scrim"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.28 }}
    >
      <motion.div
        ref={panelRef}
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="update-title"
        tabIndex={-1}
        initial={{ opacity: 0, y: 22, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE_SWIM }}
      >
        <div style={{ display: 'grid', placeItems: 'center' }}>
          <CoreconfMark size={64} title="" />
        </div>
        <h2 id="update-title">Güvenlik güncellemesi</h2>
        <p>
          {summary ??
            `Sürüm ${version} güvenlikle ilgili bir düzeltme içeriyor. Devam etmek için güncellemen gerekiyor.`}
        </p>
        <button
          className="update-cta"
          style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
          onClick={onApply}
        >
          <AppIcon name="shield" size={16} hole="transparent" />
          Güncelle ve yeniden başlat
        </button>
      </motion.div>
    </motion.div>
  );
}
